import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowRight, Kanban, Users, Building2, FileText,
  ClipboardCheck, Calendar, Zap, ShieldCheck,
  BarChart2, Target, CheckCircle,
  Briefcase, Star, TrendingUp, Clock,
} from 'lucide-react';
import './Landing.css';

/* ── Animated counter ── */
function Counter({ target, suffix = '', duration = 1800 }) {
  const [count, setCount] = useState(0);
  const ref = useRef(null);
  const started = useRef(false);

  useEffect(() => {
    const obs = new IntersectionObserver(([e]) => {
      if (e.isIntersecting && !started.current) {
        started.current = true;
        let startTime = null;
        const step = (ts) => {
          if (!startTime) startTime = ts;
          const progress = Math.min((ts - startTime) / duration, 1);
          const eased = 1 - Math.pow(1 - progress, 3);
          setCount(Math.floor(eased * target));
          if (progress < 1) requestAnimationFrame(step);
        };
        requestAnimationFrame(step);
      }
    }, { threshold: 0.5 });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, [target, duration]);

  return <span ref={ref}>{count}<span style={{ color:'#FF4100' }}>{suffix}</span></span>;
}

/* ── Scroll reveal hook ── */
function useReveal() {
  useEffect(() => {
    const els = document.querySelectorAll('.lp-reveal');
    const obs = new IntersectionObserver((entries) => {
      entries.forEach(e => { if (e.isIntersecting) e.target.classList.add('lp-visible'); });
    }, { threshold: 0.12 });
    els.forEach(el => obs.observe(el));
    return () => obs.disconnect();
  }, []);
}

/* ── Features data ── */
const FEATURES = [
  {
    icon: <Kanban size={22}/>,
    color: '#FF4100',
    bg: 'rgba(255,65,0,0.10)',
    title: 'Pipeline AO',
    desc: 'Gérez tous vos appels d\'offres en vue Kanban ou liste. Statuts, chronos, assignations et scoring IA en un coup d\'œil.',
    tag: 'Core',
    tagColor: 'rgba(255,65,0,0.14)',
    tagText: '#FF6A38',
  },
  {
    icon: <Users size={22}/>,
    color: '#22C55E',
    bg: 'rgba(34,197,94,0.10)',
    title: 'Pipe Candidats',
    desc: 'Sourcez, qualifiez et suivez vos candidats à travers toutes les étapes. Shortlist, préqualification, entretiens.',
    tag: 'Sourcing',
    tagColor: 'rgba(34,197,94,0.12)',
    tagText: '#4ADE80',
  },
  {
    icon: <Zap size={22}/>,
    color: '#A78BFA',
    bg: 'rgba(139,92,246,0.10)',
    title: 'WiCV IA',
    desc: 'Convertissez n\'importe quel CV en fiche structurée au format Wikeys. Scoring automatique et matching AO en secondes.',
    tag: 'IA',
    tagColor: 'rgba(139,92,246,0.12)',
    tagText: '#A78BFA',
  },
  {
    icon: <Building2 size={22}/>,
    color: '#38BDF8',
    bg: 'rgba(56,189,248,0.10)',
    title: 'CRM Clients',
    desc: 'Centralisez vos sociétés clientes, prospects et contacts. Historique complet des interactions et AO associés.',
    tag: 'CRM',
    tagColor: 'rgba(56,189,248,0.12)',
    tagText: '#38BDF8',
  },
  {
    icon: <ClipboardCheck size={22}/>,
    color: '#FCD34D',
    bg: 'rgba(245,158,11,0.10)',
    title: 'Rapports Techniques',
    desc: 'Les référents techniques évaluent les candidats avec des fiches structurées. Hard skills, soft skills, scoring détaillé.',
    tag: 'RT',
    tagColor: 'rgba(245,158,11,0.12)',
    tagText: '#FCD34D',
  },
  {
    icon: <Calendar size={22}/>,
    color: '#F87171',
    bg: 'rgba(239,68,68,0.10)',
    title: 'Calendrier',
    desc: 'Toutes vos deadlines AO, entretiens et actions en vue mensuelle ou agenda. Alertes urgence automatiques.',
    tag: 'Planning',
    tagColor: 'rgba(239,68,68,0.12)',
    tagText: '#F87171',
  },
];

const STEPS = [
  { num: '01', title: 'Recevez un AO', desc: 'Créez l\'AO depuis le Pipeline. Renseignez client, poste, dates limites et critères.' },
  { num: '02', title: 'Sourcez & scorez', desc: 'WiCV IA convertit et score les CVs automatiquement. Les meilleurs profils remontent en priorité.' },
  { num: '03', title: 'Préqualifiez', desc: 'TA et RT évaluent les candidats. Rapports soft/hard skills structurés en quelques minutes.' },
  { num: '04', title: 'Shortlistez & envoyez', desc: 'Validez la shortlist, générez les fiches PDF Wikeys et envoyez au client en un clic.' },
];

const ROLES = [
  {
    icon: <Briefcase size={20}/>,
    iconColor: '#FF6A38',
    color: 'rgba(255,65,0,0.10)',
    name: 'Business Manager',
    desc: 'Vue globale des AO en cours, KPIs temps réel, validation des shortlists et suivi des clients CRM.',
    tags: ['Pipeline AO', 'Dashboard', 'CRM'],
  },
  {
    icon: <Target size={20}/>,
    iconColor: '#4ADE80',
    color: 'rgba(34,197,94,0.10)',
    name: 'Talent Acquisition',
    desc: 'Sourcing, scoring WiCV, préqualification soft skills et suivi du pipe candidats par mission.',
    tags: ['Candidats', 'WiCV IA', 'Prékval.'],
  },
  {
    icon: <ClipboardCheck size={20}/>,
    iconColor: '#A78BFA',
    color: 'rgba(139,92,246,0.10)',
    name: 'Référent Technique',
    desc: 'Évaluations techniques structurées des candidats shortlistés avec notation détaillée par compétence.',
    tags: ['Rapports RT', 'Hard skills', 'Scoring'],
  },
  {
    icon: <BarChart2 size={20}/>,
    iconColor: '#38BDF8',
    color: 'rgba(56,189,248,0.10)',
    name: 'Super Admin',
    desc: 'Pilotage complet : utilisateurs, Business Units, tableaux de bord analytics et KPIs détaillés.',
    tags: ['Admin', 'Analytics', 'BUs'],
  },
  {
    icon: <Users size={20}/>,
    iconColor: '#FCD34D',
    color: 'rgba(245,158,11,0.10)',
    name: 'Ressources Humaines',
    desc: 'Gestion du vivier candidats, pipeline interne, ressources ESN et suivi des collaborateurs.',
    tags: ['Ressources', 'Pipeline', 'RH'],
  },
  {
    icon: <Building2 size={20}/>,
    iconColor: '#F87171',
    color: 'rgba(239,68,68,0.10)',
    name: "Ingénieur d'Affaires",
    desc: "Suivi des AO clients, création d'offres, CRM contacts et coordination avec les équipes talent.",
    tags: ['AO', 'CRM', 'Clients'],
  },
];

const TICKER_ITEMS = [
  'Pipeline AO', 'Matching IA', 'WiCV', 'Kanban Board', 'Shortlist',
  'Rapports RT', 'CRM Clients', 'Préqualification', 'Calendrier', 'Ressources ESN',
  'Business Units', 'Scoring Automatique', 'Fiche Wikeys PDF', 'Analytics',
];

export default function Landing() {
  const [scrolled, setScrolled] = useState(false);
  useReveal();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  return (
    <div className="lp">

      {/* ── Navbar ── */}
      <nav className={`lp-nav ${scrolled ? 'lp-nav--scrolled' : ''}`}>
        <a href="/" className="lp-nav-brand">
          <div className="lp-nav-bars">
            <span className="lp-nav-bar"/>
            <span className="lp-nav-bar"/>
            <span className="lp-nav-bar"/>
          </div>
          <div>
            <div className="lp-nav-name">WIBusiness</div>
            <div className="lp-nav-by">by Wikeys</div>
          </div>
        </a>

        <div className="lp-nav-links">
          <a href="#features"  className="lp-nav-link">Fonctionnalités</a>
          <a href="#workflow"  className="lp-nav-link">Comment ça marche</a>
          <a href="#roles"     className="lp-nav-link">Équipes</a>
        </div>

        <Link to="/login" className="lp-nav-cta">
          Se connecter <ArrowRight size={15}/>
        </Link>
      </nav>

      {/* ── Hero ── */}
      <section className="lp-hero">
        <div className="lp-orb lp-orb-1"/>
        <div className="lp-orb lp-orb-2"/>
        <div className="lp-orb lp-orb-3"/>

        {/* Barres décoratives droite */}
        <div className="lp-hero-bars">
          <span className="lp-hero-bar"/>
          <span className="lp-hero-bar"/>
          <span className="lp-hero-bar"/>
          <span className="lp-hero-bar"/>
        </div>
        <div className="lp-hero-bars-left">
          <span className="lp-hero-bar"/>
          <span className="lp-hero-bar"/>
          <span className="lp-hero-bar"/>
        </div>

        <div className="lp-hero-inner">
          <div className="lp-hero-pill">
            <span className="lp-pill-dot"/>
            Plateforme opérationnelle ESN
          </div>

          <h1 className="lp-hero-h1">
            Recrutez plus vite.
            <span className="lp-h1-accent">Pilotez chaque AO.</span>
          </h1>

          <p className="lp-hero-sub">
            WIBusiness centralise l'ensemble du workflow ESN — appels d'offres, candidats,
            CV IA, CRM et rapports techniques — dans un seul outil conçu pour vos équipes.
          </p>

          <div className="lp-hero-actions">
            <Link to="/login" className="lp-btn-primary">
              Accéder à la plateforme <ArrowRight size={17}/>
            </Link>
            <a href="#features" className="lp-btn-ghost">
              Découvrir les modules
            </a>
          </div>

          <div className="lp-hero-stats">
            <div className="lp-hero-stat">
              <div className="lp-stat-val"><Counter target={340} suffix="+"/></div>
              <div className="lp-stat-lbl">AO traités</div>
            </div>
            <div className="lp-hero-stat">
              <div className="lp-stat-val"><Counter target={94} suffix="%"/></div>
              <div className="lp-stat-lbl">Taux de matching</div>
            </div>
            <div className="lp-hero-stat">
              <div className="lp-stat-val"><Counter target={12} suffix="×"/></div>
              <div className="lp-stat-lbl">Gain de temps</div>
            </div>
            <div className="lp-hero-stat">
              <div className="lp-stat-val"><Counter target={6}/></div>
              <div className="lp-stat-lbl">Modules intégrés</div>
            </div>
          </div>
        </div>
      </section>

      {/* ── Ticker ── */}
      <div className="lp-ticker">
        <div className="lp-ticker-track">
          {[...TICKER_ITEMS, ...TICKER_ITEMS].map((item, i) => (
            <span key={i} className="lp-ticker-item">
              <span className="lp-ticker-sep">▸</span> {item}
            </span>
          ))}
        </div>
      </div>

      {/* ── Features ── */}
      <div id="features">
        <div className="lp-section">
          <div className="lp-reveal">
            <div className="lp-section-eyebrow">Fonctionnalités</div>
            <h2 className="lp-section-h2">Tout ce dont votre ESN<br/>a besoin, dans un seul outil.</h2>
            <p className="lp-section-sub">
              Six modules intégrés, conçus pour les équipes commerciales et talent.
              Aucune feuille Excel, aucun outil externe.
            </p>
          </div>

          <div className="lp-features-grid">
            {FEATURES.map((f, i) => (
              <div key={f.title} className={`lp-feat-card lp-reveal lp-reveal-d${i + 1}`}>
                <div className="lp-feat-icon" style={{ background: f.bg, color: f.color }}>
                  {f.icon}
                </div>
                <div className="lp-feat-title">{f.title}</div>
                <div className="lp-feat-desc">{f.desc}</div>
                <span className="lp-feat-tag" style={{ background: f.tagColor, color: f.tagText }}>
                  {f.tag}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="lp-divider"/>

      {/* ── Workflow / How it works ── */}
      <div id="workflow" className="lp-workflow-section lp-section-full">
        <div className="lp-section-inner">
          <div className="lp-reveal" style={{ textAlign:'center', marginBottom:0 }}>
            <div className="lp-section-eyebrow" style={{ justifyContent:'center' }}>Comment ça marche</div>
            <h2 className="lp-section-h2" style={{ textAlign:'center' }}>
              De la réception de l'AO<br/>à la shortlist client.
            </h2>
            <p className="lp-section-sub" style={{ textAlign:'center', margin:'0 auto' }}>
              Un processus fluide et traçable du premier contact jusqu'à l'envoi de la sélection finale.
            </p>
          </div>

          <div className="lp-steps">
            {STEPS.map((s, i) => (
              <div key={s.num} className={`lp-step lp-reveal lp-reveal-d${i + 1}`}>
                <div className="lp-step-num">{s.num}</div>
                <div className="lp-step-title">{s.title}</div>
                <div className="lp-step-desc">{s.desc}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="lp-divider"/>

      {/* ── Showcase AO Pipeline ── */}
      <div className="lp-section">
        <div className="lp-showcase">
          {/* Visual mock */}
          <div className="lp-showcase-visual lp-reveal">
            <div style={{ marginBottom:16, display:'flex', alignItems:'center', gap:10 }}>
              <div style={{ display:'flex', gap:5, transform:'skewX(-14deg)' }}>
                <span style={{ width:4, height:20, background:'#FF4100', borderRadius:2, display:'block' }}/>
                <span style={{ width:4, height:16, background:'#FF4100', borderRadius:2, display:'block', opacity:0.7 }}/>
                <span style={{ width:4, height:12, background:'#FF4100', borderRadius:2, display:'block', opacity:0.44 }}/>
              </div>
              <span style={{ fontSize:'0.8rem', fontWeight:700, color:'rgba(255,255,255,0.7)', letterSpacing:'-0.01em' }}>
                Pipeline AO — Vue Kanban
              </span>
            </div>
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:10 }}>
              {[
                { label:'Nouveau', color:'#38BDF8', cards:[
                  { title:'Dev Java Senior', meta:'SPW Digital · Namur', score:87, scoreColor:'#4ADE80' },
                  { title:'Chef de Projet', meta:'Accenture · Bruxelles', score:72, scoreColor:'#FCD34D' },
                ]},
                { label:'En cours', color:'#FF4100', cards:[
                  { title:'Arch. Cloud AWS', meta:'Belfius · Bruxelles', score:91, scoreColor:'#4ADE80' },
                  { title:'Data Engineer', meta:'ING · Luxembourg', score:64, scoreColor:'#FCD34D' },
                ]},
                { label:'Shortlist', color:'#4ADE80', cards:[
                  { title:'Lead React', meta:'Proximus · Liège', score:95, scoreColor:'#4ADE80' },
                ]},
              ].map(col => (
                <div key={col.label} className="lp-mini-col">
                  <div className="lp-mini-col-head">
                    <span className="lp-mini-dot" style={{ background: col.color }}/>
                    {col.label}
                  </div>
                  {col.cards.map(c => (
                    <div key={c.title} className="lp-mini-card">
                      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', gap:6 }}>
                        <div className="lp-mini-card-title">{c.title}</div>
                        <div className="lp-mini-score" style={{ background: `${c.scoreColor}1a`, border:`1.5px solid ${c.scoreColor}55`, color: c.scoreColor, fontSize:'0.6rem', fontWeight:800, minWidth:28, height:28, display:'flex', alignItems:'center', justifyContent:'center', borderRadius:'50%', flexShrink:0 }}>
                          {c.score}
                        </div>
                      </div>
                      <div className="lp-mini-card-meta">
                        <Building2 size={10} style={{ flexShrink:0 }}/>
                        {c.meta}
                      </div>
                    </div>
                  ))}
                </div>
              ))}
            </div>

            {/* IA badge flottant */}
            <div style={{ marginTop:14, padding:'10px 14px', background:'rgba(139,92,246,0.10)', border:'1px solid rgba(139,92,246,0.22)', borderRadius:10, display:'flex', alignItems:'center', gap:10 }}>
              <div style={{ width:28, height:28, borderRadius:8, background:'rgba(139,92,246,0.18)', display:'flex', alignItems:'center', justifyContent:'center', color:'#A78BFA', flexShrink:0 }}>
                <Zap size={14}/>
              </div>
              <div>
                <div style={{ fontSize:'0.72rem', fontWeight:700, color:'#A78BFA', marginBottom:2 }}>WiCV IA actif</div>
                <div style={{ fontSize:'0.66rem', color:'rgba(255,255,255,0.38)' }}>12 CVs scorés automatiquement aujourd'hui</div>
              </div>
              <div style={{ marginLeft:'auto', fontSize:'0.65rem', fontWeight:800, color:'#4ADE80', background:'rgba(34,197,94,0.12)', padding:'2px 8px', borderRadius:20 }}>LIVE</div>
            </div>
          </div>

          {/* Text */}
          <div className="lp-showcase-text lp-reveal lp-reveal-d1">
            <div>
              <div className="lp-section-eyebrow">Pipeline AO</div>
              <h2 className="lp-section-h2" style={{ fontSize:'clamp(1.8rem, 3vw, 2.6rem)' }}>
                Chaque AO, sous contrôle total.
              </h2>
            </div>
            <div className="lp-showcase-points">
              {[
                { icon: <Target size={14}/>, title: 'Kanban en temps réel', desc: 'Vue par statut avec drag & drop. Chronos, deadlines et alertes urgence intégrés.' },
                { icon: <Zap size={14}/>,    title: 'Scoring WiCV automatique', desc: 'L\'IA score chaque CV uploadé et le match instantanément avec les critères de l\'AO.' },
                { icon: <BarChart2 size={14}/>, title: 'Analytics & KPIs', desc: 'Taux de conversion, win rate, volume par BU et performance par consultant.' },
                { icon: <FileText size={14}/>, title: 'Fiches PDF Wikeys', desc: 'Générez des fiches candidat au format standard Wikeys en un clic.' },
              ].map(p => (
                <div key={p.title} className="lp-showcase-point">
                  <div className="lp-point-icon">{p.icon}</div>
                  <div>
                    <div className="lp-point-title">{p.title}</div>
                    <div className="lp-point-desc">{p.desc}</div>
                  </div>
                </div>
              ))}
            </div>
            <Link to="/login" className="lp-btn-primary" style={{ alignSelf:'flex-start' }}>
              Accéder à la plateforme <ArrowRight size={15}/>
            </Link>
          </div>
        </div>
      </div>

      <div className="lp-divider"/>

      {/* ── Roles ── */}
      <div id="roles">
        <div className="lp-section">
          <div className="lp-reveal" style={{ marginBottom: 0 }}>
            <div className="lp-section-eyebrow">Équipes</div>
            <h2 className="lp-section-h2">Conçu pour chaque rôle<br/>de votre ESN.</h2>
            <p className="lp-section-sub">
              Chaque collaborateur accède uniquement aux modules adaptés à sa mission.
            </p>
          </div>

          <div className="lp-roles-grid">
            {ROLES.map((r, i) => (
              <div key={r.name} className={`lp-role-card lp-reveal lp-reveal-d${i + 1}`}>
                <div className="lp-role-avatar" style={{ background: r.color, color: r.iconColor }}>
                  {r.icon}
                </div>
                <div className="lp-role-name">{r.name}</div>
                <div className="lp-role-desc">{r.desc}</div>
                <div className="lp-role-tags">
                  {r.tags.map(t => <span key={t} className="lp-role-tag">{t}</span>)}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ── CTA Final ── */}
      <div className="lp-cta-section">
        <div className="lp-cta-bars">
          <span className="lp-cta-bar"/>
          <span className="lp-cta-bar"/>
          <span className="lp-cta-bar"/>
          <span className="lp-cta-bar"/>
        </div>
        <div className="lp-cta-inner lp-reveal">
          <div className="lp-section-eyebrow" style={{ justifyContent:'center', marginBottom:20 }}>
            Prêt à démarrer
          </div>
          <h2 className="lp-cta-h2">
            Votre plateforme ESN<br/>vous attend.
          </h2>
          <p className="lp-cta-sub">
            Accédez à votre espace opérationnel et commencez à piloter<br/>
            vos AO, candidats et clients depuis un seul endroit.
          </p>
          <div style={{ display:'flex', alignItems:'center', justifyContent:'center', gap:14, flexWrap:'wrap' }}>
            <Link to="/login" className="lp-btn-primary" style={{ height:54, fontSize:'1.05rem' }}>
              Se connecter <ArrowRight size={18}/>
            </Link>
          </div>

          {/* Trust badges */}
          <div style={{ display:'flex', alignItems:'center', justifyContent:'center', gap:24, marginTop:36, flexWrap:'wrap' }}>
            {[
              { icon: <ShieldCheck size={14}/>, label:'Accès sécurisé' },
              { icon: <Clock size={14}/>, label:'Disponible 24/7' },
              { icon: <Star size={14}/>, label:'Données Wikeys' },
              { icon: <TrendingUp size={14}/>, label:'Mis à jour en continu' },
            ].map(b => (
              <div key={b.label} style={{ display:'flex', alignItems:'center', gap:7, fontSize:'0.78rem', color:'rgba(255,255,255,0.38)', fontWeight:500 }}>
                <span style={{ color:'rgba(255,65,0,0.60)' }}>{b.icon}</span>
                {b.label}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ── Footer ── */}
      <footer className="lp-footer">
        <a href="/" className="lp-footer-brand">
          <div className="lp-nav-bars" style={{ height:22 }}>
            <span className="lp-nav-bar" style={{ height:22 }}/>
            <span className="lp-nav-bar" style={{ height:17 }}/>
            <span className="lp-nav-bar" style={{ height:12 }}/>
          </div>
          <div>
            <div style={{ color:'rgba(255,255,255,0.55)', fontSize:'0.875rem', fontWeight:700, letterSpacing:'-0.02em' }}>WIBusiness</div>
            <div style={{ color:'rgba(255,255,255,0.22)', fontSize:'0.6rem', letterSpacing:'0.06em', textTransform:'uppercase', marginTop:1 }}>by Wikeys</div>
          </div>
        </a>

        <div className="lp-footer-copy">
          © {new Date().getFullYear()} Wikeys Group — Tous droits réservés
        </div>

        <div className="lp-footer-powered">
          <Briefcase size={12} style={{ color:'rgba(255,65,0,0.5)' }}/>
          Plateforme opérationnelle ESN
        </div>
      </footer>
    </div>
  );
}
